// src/admin/FormEditor/components/FormHeader/FormActions.jsx
// This component handles form-related actions including:
// - Exporting form data (both form metadata and report metadata)
// - Importing existing forms
// - Creating new forms (empty, default template, test template)
// The exports are validated before download and include timestamps
// Files are downloaded as JSON with the form_id as filename

import { Download, Plus, Save, Upload } from "lucide-react";
import { memo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getHongKongTimeStr } from "../../../../utils/datetimeUtil";
import {
  resetFormEditor,
  updateFormMetadata,
} from "../../store/formEditorSlice";

import { DRAFT_SAVE_URL } from "../../../../constants/urls";
import {
  migrateFormStructure,
  recursiveFillMissingLanguageContent,
} from "../../utils/formTemplates";
import {
  validateFormId,
  validateFormMetadata,
} from "../../utils/formValidation";
import NewFormDialog from "./NewFormDialog";

const ButtonBase = ({ children, variant = "default", ...props }) => {
  const baseStyle =
    "flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm font-medium";

  const variants = {
    primary: "bg-blue-50 text-blue-600 hover:bg-blue-100",
    default: "text-gray-600 hover:bg-gray-50",
  };

  return (
    <button className={`${baseStyle} ${variants[variant]}`} {...props}>
      {children}
    </button>
  );
};

const cleanSubformMetadata = (formMetadata) => {
  if (!formMetadata.subform) return formMetadata;

  // Keep only required fields for subform
  const {
    form_id,
    supported_languages,
    subform,
    createDate,
    lastUpdate,
    questions,
  } = formMetadata;

  return {
    form_id,
    supported_languages,
    subform,
    createDate,
    lastUpdate,
    questions,
  };
};

const SaveDraftSection = memo(() => {
  const formMetadata = useSelector((state) => state.formEditor.formMetadata);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);

      const formIdErrors = validateFormId(formMetadata.form_id);
      if (formIdErrors.length > 0) {
        setIsSaving(false);
        const errorMessage = [
          "Form ID validation failed:",
          "",
          ...formIdErrors,
        ].join("\n");
        alert(errorMessage);
        return;
      }

      let formMetadataToSave = {
        ...formMetadata,
        lastUpdate: getHongKongTimeStr(),
      };

      formMetadataToSave = migrateFormStructure(formMetadataToSave);
      formMetadataToSave = recursiveFillMissingLanguageContent(
        formMetadataToSave,
        formMetadataToSave.supported_languages
      );

      // Clean metadata if it's a subform
      formMetadataToSave = cleanSubformMetadata(formMetadataToSave);

      const response = await fetch(DRAFT_SAVE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          form_meta: formMetadataToSave,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to save draft");
      }

      alert("Draft saved successfully!");
    } catch (error) {
      console.error("Error saving draft:", error);
      alert(`Failed to save draft: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ButtonBase
      onClick={handleSaveDraft}
      disabled={isSaving}
      variant="default"
      title="Save Draft"
    >
      <Save size={16} />
      <span>{isSaving ? "Saving..." : "Save Draft"}</span>
    </ButtonBase>
  );
});

const ExportSection = memo(() => {
  const formMetadata = useSelector((state) => state.formEditor.formMetadata);
  const formReportMetadata = useSelector(
    (state) => state.formEditor.formReportMetadata
  );

  const downloadJSON = (data, filename) => {
    const serializedData = JSON.stringify(data, (key, value) => value, 2);
    const blob = new Blob([serializedData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    const errors = validateFormMetadata(formMetadata);

    if (errors.length > 0) {
      const errorMessage = ["Form validation failed:", "", ...errors].join(
        "\n"
      );
      alert(errorMessage);
      return;
    }

    let formMetadataToExport = {
      ...formMetadata,
      createDate: formMetadata.createDate
        ? getHongKongTimeStr(new Date(formMetadata.createDate))
        : getHongKongTimeStr(),
      lastUpdate: getHongKongTimeStr(),
    };

    formMetadataToExport = migrateFormStructure(formMetadataToExport);
    formMetadataToExport = recursiveFillMissingLanguageContent(
      formMetadataToExport,
      formMetadataToExport.supported_languages
    );

    // Clean metadata if it's a subform
    formMetadataToExport = cleanSubformMetadata(formMetadataToExport);

    const formId = formMetadata.form_id;
    const formFileName = `${formId}.json`;
    downloadJSON(formMetadataToExport, formFileName);

    if (!formMetadata.subform) {
      const formReportMetadataToExport = {
        ...formReportMetadata,
      };

      const reportFileName = `${formId}.report.json`;

      setTimeout(() => {
        downloadJSON(formReportMetadataToExport, reportFileName);

        // Create a more detailed message with instructions including filenames
        const successMessage = [
          "Form files successfully exported!",
          "",
          "Installation instructions:",
          `1. Place "${formFileName}" at:\nE:\\regform\\form_builder\\build\\regform\\forms`,
          `2. Place "${reportFileName}" at:\nE:\\regform\\form_report_meta`,
        ].join("\n");

        alert(successMessage);
      }, 100);
    } else {
      // For subforms, only show the subform placement instruction with correct path and filename
      setTimeout(() => {
        const successMessage = [
          "Subform file successfully exported!",
          "",
          "Installation instruction:",
          `Place "${formFileName}" at:\nE:\\regform\\form_builder\\build\\regform\\forms\\subforms`,
        ].join("\n");

        alert(successMessage);
      }, 100);
    }
  };

  return (
    <ButtonBase
      onClick={handleExport}
      variant="default"
      title="Export Form Files"
    >
      <Download size={16} />
      <span>Export</span>
    </ButtonBase>
  );
});

const FormActions = memo(() => {
  const [isNewFormDialogOpen, setIsNewFormDialogOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formMetadata } = useSelector((state) => state.formEditor);

  const handleCreateClick = (e) => {
    e.preventDefault();
    setIsNewFormDialogOpen(true);
  };

  const handleNewFormConfirm = (purpose) => {
    dispatch(resetFormEditor({ purpose }));
    navigate("/admin/form/new", {
      replace: true,
      state: { reset: Date.now() },
    });
    setIsNewFormDialogOpen(false);
  };

  const handleImport = (event) => {
    console.log("Importing form metadata...");
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          let importedData = JSON.parse(e.target.result);
          console.log("Imported data:", importedData);
          if (!importedData.form_id) {
            importedData.form_id = `form_${Date.now()}`;
          }

          if (!importedData.createDate) {
            importedData.createDate = getHongKongTimeStr();
          }
          importedData.lastUpdate = getHongKongTimeStr();
          importedData = migrateFormStructure(importedData);
          const updates = importedData;
          dispatch(updateFormMetadata({ updates }));
        } catch (error) {
          alert("Invalid form metadata file");
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <ButtonBase onClick={handleCreateClick} variant="primary">
        <Plus size={16} />
        <span>New</span>
      </ButtonBase>

      <SaveDraftSection />
      <ExportSection />

      <label className="cursor-pointer">
        <ButtonBase
          onClick={() => document.getElementById("fileInput").click()}
          variant="default"
        >
          <Upload size={16} />
          <span>Import</span>
        </ButtonBase>
        <input
          id="fileInput"
          type="file"
          onChange={handleImport}
          accept=".json"
          style={{ display: "none" }}
        />
      </label>

      <NewFormDialog
        isOpen={isNewFormDialogOpen}
        onClose={() => setIsNewFormDialogOpen(false)}
        onConfirm={handleNewFormConfirm}
      />
    </div>
  );
});

export default FormActions;
